import useGetMyAccountPipelines from '@/api/queries/me/useGetMyAccountPipelines';
import { useSelector } from '@/api/state';
import PipelineDisplayType from '@/ui/pages/pipelinePage/components/PipelineDisplayType';
import { useAccountUsers } from '@/utils/storeHooks';
import {
  Button,
  Divider,
  InputGroup,
  Select,
  SelectOption,
  SelectSizeVariant,
} from '@payaca/ambrosia';
import Tag from '@payaca/components/tag/Tag';
import TagSelectCombobox from '@payaca/components/tagSelectCombobox/TagSelectCombobox';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';
import { PipelinesPermissions } from '@payaca/permissions/pipelines/pipelines.permissions';
import {
  DealSearchTarget,
  ListedDealViews,
} from '@payaca/types/listedDealTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { isNullish } from '@payaca/utilities/guards';
import { Nullable } from '@payaca/utilities/types';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { ProjectsContext } from '../contextProviders/ProjectsContextProvider';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import { PipelineBadge } from '../pipelineBadge/PipelineBadge';

const ProjectFilters: FC = () => {
  // There is a lot of duplication between the filters in this component and the
  // ones in PipelinePage. They are visually the same, but use different mechanisms
  // to manage state, so some refactoring would be needed to unify them.

  const {
    requestData: { pipelineId },
  } = useContext(ProjectsContext);
  const { data: pipelines = [] } = useGetMyAccountPipelines();
  const projectsContext = useContext(ProjectsContext);

  return (
    <>
      <div className="flex flex-wrap justify-start gap-2.5 items-center m-4 pb-0">
        <div className="basis-40 xl:basis-60">
          <PipelineFilter />
        </div>

        <div className="basis-40 xl:basis-60">
          <PipelineStagesFilter />
        </div>

        <div className="basis-40 xl:basis-60">
          <AssignedUserFilter />
        </div>

        <div className="ml-auto">
          <Button
            size={'sm'}
            variant={'white'}
            className="mr-2.5"
            onClick={() => {
              projectsContext.onUpdate({
                tagIds: undefined,
                assignedUserIds: undefined,
                searchTerm: undefined,
                searchTarget: undefined,
                pipelineStages: undefined,
                displayType: ListedDealViews.TABLE,
                pipelineId: undefined,
              });
            }}
          >
            View all
          </Button>

          <PermissionGuard renderIfHasPermissions={[DealsPermissions.ADD_DEAL]}>
            <Button
              size="sm"
              onClick={() =>
                projectsContext.createProject(
                  projectsContext.requestData.pipelineId?.toString()
                )
              }
            >
              Create project
            </Button>
          </PermissionGuard>
        </div>
      </div>

      <Divider className="m-0 p-0" />

      <div className="flex flex-col gap-4 md:flex-row justify-between items-center p-4 pb-0">
        <div className="flex items-center gap-2.5 flex-wrap md:flex-nowrap">
          <Search />
          <TagsFilter />
        </div>

        <div className="self-end flex items-center gap-4">
          <PipelineDisplayType />
        </div>
      </div>
    </>
  );
};

const PipelineFilter: FC = () => {
  const {
    onUpdate,
    requestData: { pipelineId },
    displayType,
    createPipeline,
  } = useContext(ProjectsContext);

  const { data: pipelines = [] } = useGetMyAccountPipelines();

  const pipelineDropdownOptions = useMemo(() => {
    const options: SelectOption<Nullable<number>>[] = [
      ...pipelines.map((p) => ({
        label: p.title,
        value: Number(p.id),
      })),
    ];

    if (displayType === ListedDealViews.TABLE) {
      options.unshift({
        value: null,
        label: 'All projects',
      });
    }

    return options;
  }, [pipelines, displayType]);

  return (
    <PermissionGuard
      renderIfHasPermissions={[PipelinesPermissions.GET_PIPELINES_FOR_ACCOUNT]}
    >
      <Select
        sizeVariant={SelectSizeVariant.SM}
        value={isNullish(pipelineId) ? null : pipelineId}
        onChange={(value) => {
          if (value === -1) {
            createPipeline();
          } else {
            onUpdate({
              pipelineId: value === null ? undefined : +value,
              pipelineStages: undefined,
            });
          }
        }}
        options={pipelineDropdownOptions}
        CustomOption={(option) => {
          if (option.option.value === 0) {
            return <PipelineBadge />;
          } else if (
            option.option.value === null ||
            option.option.value === -1
          ) {
            return <span>{option.option.label}</span>;
          } else {
            return <PipelineBadge pipelineId={option.option.value} />;
          }
        }}
        CustomSelected={({ selectedOptions }) => {
          const option = selectedOptions?.length ? selectedOptions[0] : null;
          if (!option) return null;

          if (option.value === 0) {
            return <PipelineBadge />;
          } else if (option.value === null) {
            return <>{option.label}</>;
          } else {
            return <PipelineBadge pipelineId={option.value} />;
          }
        }}
        placeholder={'Pipeline'}
      />
    </PermissionGuard>
  );
};

const PipelineStagesFilter: FC = () => {
  const {
    onUpdate,
    requestData: { pipelineStages, pipelineId },
  } = useContext(ProjectsContext);

  const { data: pipelines = [] } = useGetMyAccountPipelines();
  const pipeline = pipelines.find((p) => Number(p.id) === pipelineId);

  const pipelineStageDropdownOptions = useMemo(() => {
    if (!pipeline) {
      const stages = pipelines.flatMap((p) => p.stages.map((x) => x.title));

      return [...new Set(stages)].map((s) => ({
        label: s,
        value: s,
      }));
    } else {
      return pipeline.stages.map((s) => ({
        label: s.title,
        value: s.title,
      }));
    }
  }, [pipeline, pipelines]);

  return (
    <PermissionGuard
      renderIfHasPermissions={[PipelinesPermissions.GET_PIPELINES_FOR_ACCOUNT]}
    >
      <Select
        sizeVariant={SelectSizeVariant.SM}
        value={pipelineStages}
        onChange={(value) => {
          onUpdate({ pipelineStages: value });
        }}
        multiple
        options={pipelineStageDropdownOptions}
        placeholder={'Stage'}
        CustomSelected={({ selectedOptions }) => {
          if (selectedOptions.length > 1) {
            return <span>Stage - {selectedOptions.length} selected</span>;
          }
          return <span>{selectedOptions?.[0]?.label}</span>;
        }}
      />
    </PermissionGuard>
  );
};

const AssignedUserFilter: FC = () => {
  const {
    onUpdate,
    requestData: { assignedUserIds },
  } = useContext(ProjectsContext);

  const accountUsers = useAccountUsers();
  const userAssignmentOptions = useMemo(() => {
    const options = accountUsers
      .filter((x) => {
        return !x.deactivatedAt || x.inviteToken;
      })
      .map((accountUser) => {
        return {
          label: `${accountUser.firstname} ${accountUser.lastname}`,
          value: accountUser.id,
        };
      });

    options.unshift({
      label: 'Unassigned',
      value: -1,
    });

    return options;
  }, [accountUsers]);

  return (
    <PermissionGuard
      renderIfHasPermissions={[
        AccountsPermissions.GET_USERS,
        DealsPermissions.GET_LISTED_DEALS,
      ]}
      requireAllPermissions={true}
    >
      <Select
        sizeVariant={SelectSizeVariant.SM}
        options={userAssignmentOptions}
        multiple
        placeholder="Owner"
        value={assignedUserIds || []}
        onChange={(value) => onUpdate({ assignedUserIds: value })}
        CustomSelected={({ selectedOptions }) => {
          if (selectedOptions.length > 1) {
            return <span>Owner - {selectedOptions.length} selected</span>;
          }
          return <span>{selectedOptions?.[0]?.label}</span>;
        }}
      />
    </PermissionGuard>
  );
};

const TagsFilter: FC = () => {
  const {
    onUpdate,
    requestData: { tagIds },
  } = useContext(ProjectsContext);

  const availableTags = useSelector((state) => state.tags.tags);

  return (
    <TagSelectCombobox
      className="w-[13.333rem]"
      sizeVariant={SelectSizeVariant.SM}
      availableTags={availableTags}
      multiple
      placeholder="Tags"
      value={tagIds || []}
      onChange={(value) => {
        onUpdate({ tagIds: value });
      }}
      CustomSelected={({ selectedOptions }) => {
        if (selectedOptions.length === 0) return null;
        if (selectedOptions.length > 1) {
          return <span>Tags - {selectedOptions.length} selected</span>;
        }
        return (
          <span className="align-center flex">
            <Tag colour={selectedOptions?.[0]?.metadata?.tagColour}>
              {selectedOptions?.[0]?.metadata?.tagText || ''}
            </Tag>
          </span>
        );
      }}
    />
  );
};

const searchTargetOptions: { label: string; value: DealSearchTarget | null }[] =
  [
    {
      label: 'Search all',
      value: null,
    },
    {
      label: 'Project',
      value: 'deal',
    },
    {
      label: 'Site address',
      value: 'dealAddresses',
    },
    {
      label: 'Customer',
      value: 'customer',
    },
    {
      label: 'Proposal ref',
      value: 'proposals',
    },
    {
      label: 'Change Proposal ref',
      value: 'changeProposals',
    },
    {
      label: 'Invoice ref',
      value: 'invoices',
    },
  ];

const Search: FC = () => {
  const {
    onUpdate,
    isLoading,
    requestData: { searchTarget, searchTerm: externalSearchTerm },
  } = useContext(ProjectsContext);

  const [searchTerm, setSearchTerm] = useState(externalSearchTerm);

  useEffect(() => {
    setSearchTerm(externalSearchTerm);
  }, [externalSearchTerm]);

  return (
    <InputGroup sizeVariant="sm">
      <InputGroup.Select
        className="w-[10.3rem]"
        options={searchTargetOptions}
        value={searchTarget || null}
        onChange={(value) => {
          onUpdate({
            searchTarget: value ? (value as DealSearchTarget) : undefined,
          });
        }}
      />
      <InputGroup.Input
        className="w-[16.8rem]"
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Search"
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            onUpdate({ searchTerm });
          }
        }}
      />
      {!!searchTerm && (
        <InputGroup.Button
          variant={'white'}
          disabled={isLoading}
          onClick={() => {
            setSearchTerm('');
            onUpdate({ searchTerm: '' });
          }}
        >
          <UntitledIcon className="h-3 w-3" name="x-close" />
        </InputGroup.Button>
      )}
      <InputGroup.Button
        variant={'white'}
        onClick={() => onUpdate({ searchTerm })}
        disabled={isLoading}
        isProcessing={isLoading}
        replaceContentWhenProcessing
      >
        <UntitledIcon className="h-4 w-4" name="search-lg.3" />
      </InputGroup.Button>
    </InputGroup>
  );
};

export default ProjectFilters;
